/**
 * #669 Balances panel: USDC (total / hold / available), outcome tokens ("+<enc>" spot rows) with side name,
 * shares, avg entry, value at mid and unrealized PnL, then other spot tokens collapsed under "Other".
 */
import { Link } from 'react-router-dom';
import { useGhost } from '../wallet/context';
import type { OutcomeHolding } from './derive';
import { fmtCents, fmtNum, fmtPct, fmtSignedUsd, fmtUsd, pnlClass } from './format';
import { marketHref, useDerivedBalances } from './useDerivedBalances';
import OtherVenuesToggle from '../venue/OtherVenuesToggle';
import { venueLabel } from '../venue/scope';
import { useVenueOutcomeRows } from '../venue/useVenueRows';
import { useT } from '../i18n/useT';

function Stat({ label, value, testId }: { label: string; value: string; testId?: string }) {
  return (
    <div>
      <div className="text-xs text-muted">{label}</div>
      <div className="num text-base sm:text-lg" data-testid={testId}>
        {value}
      </div>
    </div>
  );
}

export function SideBadge({ side, name }: { side: number; name: string }) {
  return (
    <span
      className={`inline-block max-w-[10rem] truncate rounded px-1.5 py-0.5 text-xs font-medium ${
        side === 0 ? 'bg-yes/15 text-yes' : 'bg-no/15 text-no'
      }`}
      title={name}
      translate="no"
    >
      {name}
    </span>
  );
}

export function OutcomeHoldingsTable({
  rows,
  testId,
  onClose,
}: {
  rows: OutcomeHolding[];
  testId: string;
  /** #873 Positions tab, own account only: a "Close" button per row (opens the ticket prefilled, never submits). */
  onClose?: (r: OutcomeHolding) => void;
}) {
  const { ghostAddress } = useGhost();
  const t = useT();
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[40rem] text-sm" data-testid={testId}>
        <thead className="text-start text-xs text-muted">
          <tr>
            <th className="py-2 pe-3 font-normal">{t('common.market')}</th>
            <th className="py-2 pe-3 font-normal">{t('balances.col.side')}</th>
            <th className="py-2 pe-3 text-end font-normal">{t('common.shares')}</th>
            <th className="py-2 pe-3 text-end font-normal">{t('balances.col.avg_entry')}</th>
            <th className="py-2 pe-3 text-end font-normal">{t('common.mid')}</th>
            <th className="py-2 pe-3 text-end font-normal">{t('balances.col.value')}</th>
            <th className="py-2 text-end font-normal">{t('balances.col.upnl')}</th>
            {onClose && (
              <th className="py-2 ps-3 text-end font-normal">
                <span className="sr-only">{t('positions.close.button')}</span>
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.balanceCoin} className="border-t border-border" data-testid={`outcome-row-${r.balanceCoin}`}>
              <td className="py-2 pe-3">
                <Link to={marketHref(r.outcome, r.side, ghostAddress)} className="hover:text-accent hover:underline">
                  <span className="block max-w-[16rem] truncate" translate="no">{r.title}</span>
                  <span className="text-xs text-muted">#{r.outcome}</span>
                </Link>
              </td>
              <td className="py-2 pe-3">
                <SideBadge side={r.side} name={r.sideName} />
              </td>
              <td className="num py-2 pe-3 text-end">
                {fmtNum(r.shares, 2)}
                {r.hold > 0 && (
                  <div className="text-xs text-muted" data-testid="in-orders">
                    {t('balances.in_orders', { n: fmtNum(r.hold, 2) })}
                  </div>
                )}
              </td>
              <td className="num py-2 pe-3 text-end">{fmtCents(r.avgEntry)}</td>
              <td className="num py-2 pe-3 text-end">{fmtCents(r.mid)}</td>
              <td className="num py-2 pe-3 text-end">{fmtUsd(r.value)}</td>
              <td className={`num py-2 text-end ${pnlClass(r.upnl)}`}>
                {fmtSignedUsd(r.upnl)}
                <div className="text-xs">{fmtPct(r.upnlPct)}</div>
              </td>
              {onClose && (
                <td className="py-2 ps-3 text-end">
                  <button
                    type="button"
                    onClick={() => onClose(r)}
                    aria-label={t('positions.close.aria', { side: r.sideName, title: r.title })}
                    className="rounded-md border border-no/50 px-2 py-1 text-xs font-semibold text-no hover:bg-no/10"
                    data-testid={`close-${r.balanceCoin}`}
                  >
                    {t('positions.close.button')}
                  </button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function BalancesPanel({ address }: { address: string }) {
  const { derived, isLoading, error } = useDerivedBalances(address);
  const { usdc, outcomes, other, outcomeValue, outcomeUpnl } = derived;
  const scoped = useVenueOutcomeRows(outcomes);
  const t = useT();

  return (
    <section className="space-y-4" aria-label={t('portfolio.tab.balances')} data-testid="balances-panel">
      {error && !isLoading && (
        <div className="card border-danger/50 p-3 text-sm text-danger">{t('balances.load_error', { error: error.message })}</div>
      )}
      <div className="card grid grid-cols-2 gap-4 p-4 sm:grid-cols-4">
        <Stat label={t('balances.usdc_total')} value={isLoading ? '…' : fmtUsd(usdc.total)} testId="usdc-total" />
        <Stat label={t('balances.in_open_orders')} value={isLoading ? '…' : fmtUsd(usdc.hold)} testId="usdc-hold" />
        <Stat label={t('balances.available')} value={isLoading ? '…' : fmtUsd(usdc.available)} testId="usdc-available" />
        <Stat label={t('balances.outcome_value')} value={isLoading ? '…' : fmtUsd(outcomeValue)} testId="outcome-value" />
      </div>

      <div className="card p-4">
        <div className="mb-2 flex items-baseline justify-between gap-2">
          <h2 className="font-semibold">{t('balances.outcome_tokens')}</h2>
          {outcomes.length > 0 && (
            <span className={`num text-sm ${pnlClass(outcomeUpnl)}`}>{t('balances.upnl', { value: fmtSignedUsd(outcomeUpnl) })}</span>
          )}
        </div>
        <OtherVenuesToggle v={scoped.v} hidden={scoped.hidden} className="mb-2" />
        {isLoading ? (
          <p className="text-sm text-muted">{t('common.loading')}</p>
        ) : scoped.visible.length === 0 ? (
          <p className="text-sm text-muted" data-testid="no-outcome-tokens">
            {outcomes.length === 0 ? t('balances.no_tokens') : t('balances.no_tokens_venue', { venue: venueLabel(scoped.v.venue) })}
          </p>
        ) : (
          <OutcomeHoldingsTable rows={scoped.visible} testId="outcome-balances" />
        )}
      </div>

      {other.length > 0 && (
        <details className="card p-4" data-testid="other-balances">
          <summary className="cursor-pointer font-semibold">
            {t('balances.other')} <span className="text-sm font-normal text-muted">{t('balances.other_count', { n: other.length })}</span>
          </summary>
          <div className="mt-2 overflow-x-auto">
            <table className="w-full min-w-[28rem] text-sm">
              <thead className="text-start text-xs text-muted">
                <tr>
                  <th className="py-2 pe-3 font-normal">{t('balances.col.token')}</th>
                  <th className="py-2 pe-3 text-end font-normal">{t('common.total')}</th>
                  <th className="py-2 pe-3 text-end font-normal">{t('balances.col.in_orders')}</th>
                  <th className="py-2 text-end font-normal">{t('balances.col.cost_basis')}</th>
                </tr>
              </thead>
              <tbody>
                {other.map((o) => (
                  <tr key={o.coin} className="border-t border-border">
                    <td className="py-2 pe-3" translate="no">{o.coin}</td>
                    <td className="num py-2 pe-3 text-end">{fmtNum(o.total, 8)}</td>
                    <td className="num py-2 pe-3 text-end">{fmtNum(o.hold, 8)}</td>
                    <td className="num py-2 text-end">{fmtUsd(o.entryNtl)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
      )}
    </section>
  );
}
